/**
 * Labstep
 *
 * @module components/ProtocolCondition/SmartTable/Modal
 * @desc Protocol Condition Modal
 */

import EntitySearch from 'labstep-web/components/Entity/Search';
import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { EntityViewFormShowEditName } from 'labstep-web/components/EntityView/Form/ShowEdit/Name';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { ModalDefaultHeader as ModalHeader } from 'labstep-web/core/Modal/Default/Header';
import { DataGridExportAsCSV } from 'labstep-web/grid/Index/components/DataGridExportAsCSV';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridIndexActionShowSideBar as EntityDataGridActionShowSideBar } from 'labstep-web/grid/Index/components/ShowSideBar';
import { ToolPanelProvider } from 'labstep-web/grid/Index/components/ToolPanel/context';
import {
  getSystemFilterExperimentGuid,
  getSystemFilterProtocolGuid,
} from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type React from 'react';
import { ProtocolConditionSmartTableModalContent } from './Content';
import styles from './styles.module.scss';
import type { ProtocolConditionSmartTableModalProps } from './types';

export const ProtocolConditionSmartTableModal: React.FC<
  ProtocolConditionSmartTableModalProps
> = ({
  experimentOrProtocol,
  entityView,
  open = false,
  onClose,
  viewComponent,
}) => {
  const { activeGroup } = useActiveGroup();

  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    count: 100,
    serializerGroups: 'smart_table,paginator',
  };

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'smart_table',
    injectFields: {
      [experimentOrProtocol.entityName]: {
        id: experimentOrProtocol.id,
      },
    },
    globalParams,
    entityView,
    initialSearchParams:
      GridFilterService.injectSystemFilter(
        experimentOrProtocol instanceof Experiment
          ? getSystemFilterExperimentGuid(experimentOrProtocol.guid)
          : getSystemFilterProtocolGuid(experimentOrProtocol.guid),
        entityView.parameters,
      ) || {},
    loadedEntityView: new EntityView({
      ...entityView,
    }),
  };
  if (paramsStateContext.loadedEntityView) {
    paramsStateContext.loadedEntityView.parameters =
      paramsStateContext.initialSearchParams;
  }

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <ToolPanelProvider>
          <EntitySearch
            entityName={ProtocolCondition.entityName}
            useAllPages
            hideFilters
            hideSearch
            bulkActions={[BulkAction.delete]}
            showEmptyStateOnSearchOnly
          >
            {({
              entities,
              readNextPage,
              status,
            }: EntitySearchContainerChildrenProps) => {
              const gridSmartTableProtocolConditionService =
                useGridSmartTableProtocolConditionServiceFromContext();

              return (
                <Modal
                  className={styles.modal}
                  dataCy="components/ProtocolCondition/Modal"
                  header={({ toggleModal }) => (
                    <ModalHeader
                      text={
                        !entityView.id ? (
                          <Flex>
                            <Icon name={'random'} />
                            {entityView.name ? (
                              <span>{entityView.name}</span>
                            ) : (
                              'Conditions'
                            )}
                          </Flex>
                        ) : (
                          <EntityViewFormShowEditName
                            entityView={entityView}
                          />
                        )
                      }
                      onClose={() => {
                        if (onClose) {
                          onClose();
                        }
                        if (
                          entityView.id &&
                          gridSmartTableProtocolConditionService
                        ) {
                          gridSmartTableProtocolConditionService.updateEntityViewColIds(
                            entityView,
                          );
                        }

                        toggleModal();
                      }}
                      extraActions={
                        experimentOrProtocol.isLocked ||
                        entityView.name === 'Conditions Table'
                          ? [
                              <DataGridExportAsCSV
                                className={styles.searchBarAction}
                              />,
                            ]
                          : [
                              <EntityDataGridActionShowSideBar
                                className={styles.searchBarAction}
                              />,
                              <DataGridExportAsCSV
                                className={styles.searchBarAction}
                              />,
                            ]
                      }
                    />
                  )}
                  size="fullscreen"
                  closeIcon={false}
                  initialModalState={open}
                  onClose={onClose}
                  content={
                    <ProtocolConditionSmartTableModalContent
                      experimentOrProtocol={experimentOrProtocol}
                      entityView={entityView}
                      group={activeGroup!}
                      entities={entities}
                      readNextPage={readNextPage!}
                      status={status}
                    />
                  }
                  viewComponent={viewComponent}
                />
              );
            }}
          </EntitySearch>
        </ToolPanelProvider>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};
